/* eslint-disable @typescript-eslint/promise-function-async */
import {randomString} from '@shared/utils/math';
import {isString} from 'lodash-es';
import type {DefineComponent} from 'vue';
import {markRaw} from 'vue';
import ConfirmDialogVue from './ConfirmDialog.vue';

type GenericVueComp = DefineComponent<any, any, any, any, any, any, any, any, any, any, any>;
interface IGenericVueSlotData {
   comp: GenericVueComp;
   props: InstanceType<GenericVueComp>['$props'];
}

/**
 * Data for a modal.
 */
export interface IDialogData<C extends GenericVueComp> {
   /** Unique string to use as key */
   id: string;

   /** The component type to use. */
   comp: C;

   /** Props to bind to the component. */
   props: any; // todo: Tighten this up

   /**
    * Optional component and props to use in the default slot of a dialog
    */
   slot?: IGenericVueSlotData;

   // confirm/cancel close the dialog and resolve show(); close only removes it, leaving show() pending.
   confirm: (data: any) => void;
   cancel: (data: any) => void;
   close: () => void;
}

export interface IShowDialogRet<D> {
   canceled: boolean;
   data: D;
}

export interface ISnackbarOptions {
   message: string;
   type?: 'success' | 'info' | 'warning' | 'error';
   timeout?: number;
   /** If true, replace any currently showing snackbars with this one. */
   replace?: boolean;
}

export interface ISnackbarData extends ISnackbarOptions {
   id: string;
   visible: boolean;

   /** Present on undo toasts; runs when the user clicks Undo. */
   onUndo?: () => void;
}

export interface IUndoSnackbarOptions {
   /** Fact copy stating what happened, e.g. "List deleted". */
   message: string;

   /** Runs when the user clicks Undo before the toast expires or is replaced. */
   onUndo: () => void;
}

/**
 * Shows dialog components on a stack rendered by DialogHolder, which must be
 * mounted in the root app component. Also queues snackbar notifications.
 *
 * A dialog component emits `confirm` or `cancel` with the data to return to
 * the caller. It must emit `cancel` on every dismissal (click outside, escape
 * key); otherwise the dialog stays on the stack.
 */
export class DialogService {
   /** Stack of dialogs to show. */
   protected dialogStack: IDialogData<any>[] = [];

   /** Callbacks run whenever either stack changes. */
   protected stackChangeCallbacks: (() => void)[] = [];

   /** Snackbar notifications to show, in creation order. */
   public snackbarStack: ISnackbarData[] = [];

   /** Show a dialog; the promise resolves when it emits confirm or cancel. */
   show<D, C = any>(
      comp: C,
      props: Record<string, any>,
      slot?: IGenericVueSlotData,
   ): Promise<IShowDialogRet<D>> {
      const dialog_id = randomString(10);

      // eslint-disable-next-line avoid-new
      const ret_prom = new Promise<IShowDialogRet<D>>((resolve, _reject) => {
         const modal_data: IDialogData<C> = {
            id: dialog_id,
            comp: markRaw(comp as any) as any,
            props,
            slot,
            close: () => {
               this.removeModal(dialog_id);
            },
            confirm: (data: any) => {
               this.removeModal(dialog_id);
               resolve({canceled: false, data});
            },
            cancel: (data: any) => {
               this.removeModal(dialog_id);
               resolve({canceled: true, data});
            },
         };

         this.pushModal(modal_data);
      });

      return ret_prom;
   }

   /**
    * Return true iff the user confirms the dialog.
    */
   async showConfirmDialog(
      props: InstanceType<typeof ConfirmDialogVue>['$props'],
      slot?: IGenericVueSlotData,
   ): Promise<boolean> {
      const ret = await this.show(ConfirmDialogVue, props, slot);
      return !ret.canceled;
   }

   /** Show a snackbar; a plain string shows as a success message for 3 seconds. */
   showSnackbar(options: ISnackbarOptions | string): void {
      const id = randomString(10);
      let snackbarData: ISnackbarData;

      if (isString(options)) {
         snackbarData = {
            id,
            message: options,
            type: 'success',
            timeout: 3000,
            visible: true,
         };
      } else {
         if (options.replace) {
            const snackbarIds = this.getSnackbarStack().map((s) => s.id);
            for (const id of snackbarIds) {
               this.removeSnackbar(id);
            }
         }

         snackbarData = {
            id,
            visible: true,
            message: options.message,
            type: options.type ?? 'success',
            timeout: options.timeout ?? 3000,
         };
      }
      this.snackbarStack.push(snackbarData);
      this.triggerCallbacks();
   }

   /**
    * Show an undo toast for ~6 seconds. At most one shows at a time: a new one
    * replaces the current one, and a replaced or expired toast's undo lapses.
    */
   showUndoSnackbar(options: IUndoSnackbarOptions): void {
      const undo_ids = this.snackbarStack.filter((s) => s.onUndo != null).map((s) => s.id);
      for (const id of undo_ids) {
         this.removeSnackbar(id);
      }

      this.snackbarStack.push({
         id: randomString(10),
         visible: true,
         message: options.message,
         timeout: 6000,
         onUndo: options.onUndo,
      });
      this.triggerCallbacks();
   }

   /**
    * @internal
    * Remove a snackbar by id; no-op when absent.
    */
   removeSnackbar(id: string): void {
      const idx = this.snackbarStack.findIndex((s) => s.id === id);
      if (idx !== -1) {
         this.snackbarStack.splice(idx, 1);
         this.triggerCallbacks();
      }
   }

   /**
    * @internal
    */
   pushModal(dialog: IDialogData<any>): void {
      this.dialogStack.push(dialog);
      this.triggerCallbacks();
   }

   /**
    * @internal
    */
   removeModal(id: string): void {
      const idx = this.dialogStack.findIndex((m) => m.id === id);
      if (idx !== -1) {
         this.dialogStack.splice(idx, 1);
      }
      this.triggerCallbacks();
   }

   /** Clear every dialog. Their show() promises never resolve. */
   removeAllDialogs(): void {
      this.dialogStack = [];
      this.triggerCallbacks();
   }

   /**
    * @internal
    */
   getStack(): IDialogData<any>[] {
      return this.dialogStack;
   }

   /**
    * @internal
    */
   getSnackbarStack(): ISnackbarData[] {
      return this.snackbarStack;
   }

   // --- Internal Interface -- //
   /**
    * @internal
    * Register a callback run whenever the dialog or snackbar stack changes.
    */
   addCallback(callback: () => void): void {
      this.stackChangeCallbacks.push(callback);
   }

   protected triggerCallbacks(): void {
      this.stackChangeCallbacks.forEach((cb) => cb());
   }
}

/**
 * Global instance of the dialog service.
 */
export const gDialogService = new DialogService();
